import {
  Box,
  Button,
  Checkbox,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Menu,
  IconButton,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import React, { Dispatch, SetStateAction, useState, useRef } from "react";
import { TextValidator, ValidatorForm } from "react-material-ui-form-validator";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../redux/store";
import { updateOrg } from "../../../redux/features/organization/organization-slice";
import { OrgService } from "../../../constants/company-user";
import { useDescope, useUser } from "@descope/react-sdk";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { OrgData } from "src/types/company";

interface IOrganizationEditForm {
  currentOrgData: OrgData;
  closeModal: Dispatch<SetStateAction<boolean>>;
}

const OrganizationEditForm: React.FC<IOrganizationEditForm> = ({
  currentOrgData,
  closeModal,
}) => {
  // #region variables region
  const { user } = useUser();

  const sdk = useDescope();

  const currentTenantId = user?.customAttributes?.currentTenantId;

  //org name
  const [orgName, setOrgName] = useState<string>(currentOrgData.orgName || "");

  //org url
  const [orgUrl, setOrgUrl] = useState<string>(currentOrgData.orgUrl || "");

  //org type
  const [orgService, setOrgService] = useState(currentOrgData.orgService || []);

  //organization supportEmail
  const [supportEmail, setSupportEmail] = useState<string>(
    currentOrgData.supportEmail || ""
  );

  const [outBoundEmailList, setOutBoundEmailList] = useState(
    currentOrgData.mailboxConnectedEmails || []
  );

  const [termsOfServiceLink, setTermsOfServiceLink] = useState<string>(
    currentOrgData.termsOfServiceLink || ""
  );

  const [privacyPolicyLink, setPrivacyPolicyLink] = useState<string>(
    currentOrgData.privacyPolicyLink || ""
  );

  const [orgPhoneNumber, setOrgPhoneNumber] = useState<string>(
    currentOrgData?.orgPhone || ""
  );

  const uniqueOutBoundEmailList = [...new Set(outBoundEmailList)];

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleDropdownClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  //organization supportEmail

  //dispatch for redux
  const dispatch = useDispatch<AppDispatch>();

  // #endregion
  // #region methods region

  //handles form submission
  const handleSubmit = async () => {
    const { payload } = await dispatch(
      updateOrg({
        name: orgName,
        url: orgUrl,
        tenantId: currentTenantId,
        service: orgService,
        supportEmail: supportEmail,
        privacyPolicyLink,
        termsOfServiceLink,
        orgPhone: orgPhoneNumber,
      })
    );

    if (payload === 1) {
      await sdk.me();
    }
    closeModal(false);
  };

  //removes focus when enter is pressed
  const removeFocus = (id: string) => {
    document.getElementById(id)?.blur();
  };

  // #endregion
  return (
    <ValidatorForm onSubmit={handleSubmit}>
      <div className="stepperForm__inner p-30px">
        {/* <h1 className="stepperForm__title p-0">Employment Information</h1> */}
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Box className="fromGroup">
              <InputLabel className="fromLabel">Company Name</InputLabel>
              <TextValidator
                id="orgName"
                type="text"
                name="orgName"
                value={orgName}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    removeFocus("orgName");
                  }
                }}
                onChange={(e: any) => setOrgName(e.target.value)}
                //validators={["required", "matchRegexp:^[a-zA-Z- ]*$"]}
                validators={["required"]}
                errorMessages={["Organization name is required"]}
              />
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Box className="fromGroup">
              <InputLabel className="fromLabel">Company Url</InputLabel>
              <TextValidator
                id="orgUrl"
                type="text"
                name="orgUrl"
                value={orgUrl}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    removeFocus("orgUrl");
                  }
                }}
                onChange={(e: any) => setOrgUrl(e.target.value)}
              />
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Box className="fromGroup">
              <InputLabel className="fromLabel">Company Services</InputLabel>
              <Select
                multiple
                id="orgUrl"
                type="text"
                name="orgUrl"
                value={orgService}
                renderValue={(value) => value.join(",")}
                onChange={(e: any) => setOrgService(e.target.value)}
              >
                {Object.values(OrgService).map((item) => (
                  <MenuItem key={item} value={item}>
                    <span>
                      <Checkbox checked={orgService.includes(item)} /> {item}
                    </span>
                  </MenuItem>
                ))}
              </Select>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Box className="fromGroup">
              <InputLabel className="fromLabel">Outbound Email</InputLabel>
              <Box sx={{ position: "relative" }}>
                <TextField
                  id="supportEmail"
                  name="supportEmail"
                  value={supportEmail}
                  onChange={(e) => setSupportEmail(e.target.value)}
                  fullWidth
                  variant="outlined"
                  error={!supportEmail}
                  helperText={!supportEmail ? "Outbound email is required" : ""}
                  inputRef={inputRef}
                  InputProps={{
                    endAdornment: (
                      <IconButton onClick={handleDropdownClick}>
                        <ArrowDropDownIcon />
                      </IconButton>
                    )
                  }}
                />
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleClose}
                  anchorOrigin={{
                    vertical: 'bottom', // Aligns the dropdown below the input
                    horizontal: 'right', // Aligns it with the left of the input
                  }}
                  transformOrigin={{
                    vertical: 'top', // Ensures the dropdown opens upwards from the bottom of the input
                    horizontal: 'right', // Aligns the dropdown with the left of the input
                  }}
                >
                  {uniqueOutBoundEmailList.map((email) => (
                    <MenuItem
                      key={email}
                      onClick={() => {
                        setSupportEmail(email);
                        handleClose();
                      }}
                    >
                      {email}
                    </MenuItem>
                  ))}
                </Menu>
              </Box>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Box className="fromGroup">
              <InputLabel className="fromLabel">
                Terms of Service Url
              </InputLabel>
              <TextValidator
                id="termsOfServiceLink"
                type="text"
                name="termsOfServiceLink"
                value={termsOfServiceLink}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    removeFocus("termsOfServiceLink");
                  }
                }}
                onChange={(e: any) => setTermsOfServiceLink(e.target.value)}
              />
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Box className="fromGroup">
              <InputLabel className="fromLabel">Privacy Policy Url</InputLabel>
              <TextValidator
                id="privacyPolicyLink"
                type="text"
                name="privacyPolicyLink"
                value={privacyPolicyLink}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    removeFocus("privacyPolicyLink");
                  }
                }}
                onChange={(e: any) => setPrivacyPolicyLink(e.target.value)}
              />
            </Box>
          </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
            <Box className="fromGroup">
              <InputLabel className="fromLabel">Company Phone Numeber</InputLabel>
              <TextValidator
                id="orgPhoneNumber"
                type="text"
                name="orgPhoneNumber"
                value={orgPhoneNumber}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    removeFocus("orgPhoneNumber");
                  }
                }}
                onChange={(e: any) => setOrgPhoneNumber(e.target.value)}
              />
            </Box>
          </Grid>
        </Grid>
      </div>
      <Box className="stepperForm__footer b-0">
        <Box sx={{ flex: "1 1 auto" }} />
        <Button type="submit" className="button">
          Save Changes
        </Button>
      </Box>
    </ValidatorForm>
  );
};

export default OrganizationEditForm;
